import { PencilIcon, PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';

import type { DamageType } from '@/common/damage';
import { ROLE } from '@/common/role';
import type { WorkPackageItem } from '@/common/work-package';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { AddLineItemDialog } from './AddLineItemDialog';
import { ApplyPriceList } from './ApplyPriceList';
import { DamageListDialog } from './DamageListDialog';
import { LineItemRow } from './LineItemRow';
import { NewLineItemRow } from './NewLineItemRow';

type NewLineItemType = Pick<WorkPackageItem, 'id' | 'description' | 'parentItemId' | 'damage' | 'damageId'>;

export type WorkPackageItemsTableProps = {
	lineItems: WorkPackageItem[];
	setLineItems: React.Dispatch<React.SetStateAction<WorkPackageItem[]>>;
	vatRate: number;
	setVatRate: React.Dispatch<React.SetStateAction<number>>;
};
export function WorkPackageItemsTable({ lineItems, setLineItems, vatRate, setVatRate }: WorkPackageItemsTableProps) {
	const { t } = useTranslation();
	const [newLineItems, setNewLineItems] = useState([] as NewLineItemType[]);
	const [isEditingVat, setIsEditingVat] = useState(false);

	// Line items - now with parent-child support
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

	const [pendingParentId, setPendingParentId] = useState<string | undefined>(undefined);
	const [isAddChoiceDialogOpen, setIsAddChoiceDialogOpen] = useState(false);
	const [damageListOpen, setDamageListOpen] = useState(false);
	const [linkDamagePendingItem, setLinkDamagePendingItem] = useState<string | undefined>(undefined);

	const handleUpdateItem = (id: string, field: keyof WorkPackageItem, value: unknown) => {
		setLineItems((prevLineItems) => prevLineItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
	};
	const handleUpdateItemV2 = (id: string, obj: Partial<WorkPackageItem>) => {
		setLineItems((prevLineItems) => prevLineItems.map((item) => (item.id === id ? { ...item, ...obj } : item)));
	};
	const calculateSubtotal = () => {
		return lineItems.reduce((sum, item) => sum + (item.quantity || 0) * Number(item.unitPrice || 0), 0);
	};
	const netAmount = calculateSubtotal();
	const taxAmount = netAmount * (vatRate / 100); // VAT
	const grossAmount = netAmount + taxAmount;
	const onAddNewLineItem = (damages?: DamageType[]) => {
		if (damages?.length) {
			const newItems = damages.map((damage) => ({
				id: `wpi_${uuid()}`,
				description: damage.title || '',
				parentItemId: pendingParentId || null,
				damageId: damage.id || null,
				damage: damage,
			}));
			setNewLineItems((prev) => [...prev, ...newItems]);
		}
		if (!damages?.length) {
			setNewLineItems((prev) => [
				...prev,
				{
					id: `wp_${uuid()}`,
					description: '',
					parentItemId: pendingParentId || null,
				},
			]);
		}
		if (pendingParentId) {
			// Auto-expand parent
			setExpandedItems(new Set([...Array.from(expandedItems), pendingParentId]));
		}
		setPendingParentId(undefined);
		setIsAddChoiceDialogOpen(false);
	};
	useEffect(() => {
		if (lineItems.length > 0 && expandedItems.size === 0) {
			// Auto-expand all items which have children
			const parentsWithChildren = new Set(
				lineItems
					.filter((item) => lineItems.some((child) => child.parentItemId === item.id))
					.map((item) => item.id as string),
			);
			setExpandedItems(parentsWithChildren);
		}
	}, [lineItems, expandedItems.size]);

	// Prepare set of already added damage IDs
	const alreadyAddedDamagesIds = useMemo(() => {
		const ids: Set<string> = new Set();
		lineItems.map((item) => ids.add(item.damageId || ''));
		newLineItems.map((item) => ids.add(item.damageId || ''));
		// Remove empty string if exists
		ids.delete('');
		return ids;
	}, [lineItems, newLineItems]);
	const handleAddRow = () => {
		setPendingParentId(undefined);
		setIsAddChoiceDialogOpen(true);
	};

	const handleAddChildItem = (parentId: string) => {
		setPendingParentId(parentId);
		setIsAddChoiceDialogOpen(true);
	};
	const onSaveNewLineItem = ({ id, description }: { description: string; id: string }) => {
		if (!description.trim()) {
			toast.error(t('finance.items.descriptionRequired'));
			return;
		}
		const newItem = newLineItems.find((item) => item.id === id);
		if (!newItem) return;

		const newItemData: WorkPackageItem = {
			id,
			description,
			quantity: newItem?.damage?.lengthArea?.length || 0,
			unitType: newItem?.damage?.lengthArea?.massureType || '',
			unitPrice: 0,
			totalPrice: 0,
			parentItemId: newItem.parentItemId || null,
			damage: newItem.damage || null,
			damageId: newItem.damageId || null,
		};
		setLineItems((prev) => [...prev, newItemData]);
		setNewLineItems((prev) => prev.filter((item) => item.id !== newItemData.id));
	};
	const onRemoveNewLineItem = (id: string) => {
		setNewLineItems((prev) => prev.filter((item) => item.id !== id));
	};
	const onToggleItemExpansion = (itemId: string) => {
		const newExpanded = new Set(expandedItems);
		if (newExpanded.has(itemId)) {
			newExpanded.delete(itemId);
		} else {
			newExpanded.add(itemId);
		}
		setExpandedItems(newExpanded);
	};
	const handleDeleteItem = (id: string) => {
		// Collect the item and all of its descendants first, then remove them in one
		// update. This ensures that deleting a parent item also deletes all of its children.
		const idsToDelete = new Set<string>([id]);
		const queue = [id];
		while (queue.length > 0) {
			const parentId = queue.shift();
			for (const item of lineItems) {
				if (item.parentItemId === parentId && item.id && !idsToDelete.has(item.id)) {
					idsToDelete.add(item.id);
					queue.push(item.id);
				}
			}
		}

		setLineItems((prevLineItems) => prevLineItems.filter((item) => !idsToDelete.has(item.id as string)));
		setExpandedItems((prevExpanded) => {
			const newExpanded = new Set(prevExpanded);
			for (const deletedId of idsToDelete) newExpanded.delete(deletedId);
			return newExpanded;
		});
	};
	const handleLinkDamageItems = (id: string) => {
		setLinkDamagePendingItem(id);
		setDamageListOpen(true);
		setIsAddChoiceDialogOpen(false);
	};
	const onDamageSelect = (damages: DamageType[]) => {
		if (!linkDamagePendingItem) return;
		const damage = damages[0];
		handleUpdateItemV2(linkDamagePendingItem, {
			damageId: damage.id || null,
			damage: damage,
			quantity: damage.lengthArea?.length || 0,
			unitType: damage.lengthArea?.massureType || '',
			unitPrice: 0,
		});
		setLinkDamagePendingItem(undefined);
		setDamageListOpen(false);
	};
	// Get root level items (those without a parent)
	const rootItems = lineItems.filter((item) => !item.parentItemId);
	const rootNewItems = newLineItems.filter((item) => !item.parentItemId);
	return (
		<>
			<div className="flex-1 overflow-auto bg-background m-0">
				<div className="space-y-4 max-w-full">
					<div className="rounded-lg border text-card-foreground bg-card shadow-sm">
						<div className="p-6 pt-6">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-6">
									<div className="flex items-center gap-2">
										<span className="text-muted-foreground text-sm">{t('finance.items.subtotal')}</span>
										<span className="font-medium text-sm">{formatCurrency(netAmount)}</span>
									</div>

									<div className="h-8 w-px bg-muted-foreground"></div>

									<div className="flex items-center gap-2">
										<span className="text-muted-foreground text-sm">{t('finance.items.vatLabel')}</span>
										<div className="flex items-center gap-1">
											{isEditingVat ? (
												<Input
													type="number"
													value={vatRate}
													onChange={(e) => setVatRate(parseFloat(e.target.value) || 0)}
													onBlur={() => setIsEditingVat(false)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' || e.key === 'Escape') {
															setIsEditingVat(false);
														}
													}}
													className="bg-input h-8 w-16 text-sm"
													autoFocus
												/>
											) : (
												<div className="flex items-center gap-1">
													<span className="text-sm font-medium">{vatRate}%</span>
													<Restricted to={ROLE.WORK_PACKAGES_UPDATE}>
														<Button
															size="icon-sm"
															variant="ghost"
															onClick={() => {
																setIsEditingVat(true);
															}}
															className="size-6 p-0 hover:bg-background!"
															title={t('finance.items.editVatRate')}
														>
															<PencilIcon className="size-3 text-muted-foreground" />
														</Button>
													</Restricted>
												</div>
											)}
										</div>
										<span className="text-sm text-muted-foreground">:</span>
										<span className="text-sm font-medium">{formatCurrency(taxAmount)}</span>
									</div>

									<div className="h-8 w-px text-muted-foreground"></div>

									<div className="flex items-center gap-2">
										<span className="text-muted-foreground text-sm font-semibold">{t('finance.items.total')}</span>
										<span className="font-semibold text-sm text-primary">{formatCurrency(grossAmount)}</span>
									</div>
								</div>
								<ApplyPriceList />
							</div>
						</div>
					</div>
					<div className="bg-card rounded-lg border overflow-hidden shadow-sm">
						<div className="relative w-full overflow-auto">
							<Table>
								<TableHeader>
									<TableRow className="border-b transition-colors bg-background hover:bg-background">
										<TableHead className="font-semibold w-12.5">{t('finance.items.position')}</TableHead>
										<TableHead className="font-semibold">{t('common.labels.description')}</TableHead>
										<TableHead className="font-semibold w-25">{t('finance.items.quantity')}</TableHead>
										<TableHead className="font-semibold w-25">{t('finance.items.unit')}</TableHead>
										<TableHead className="font-semibold w-30">{t('finance.items.unitPrice')}</TableHead>
										<TableHead className="font-semibold w-30">{t('finance.items.totalColumn')}</TableHead>
										<TableHead className="w-12.5"></TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rootItems.map((lineItem, index) => (
										<LineItemRow
											count={index + 1}
											key={lineItem.id}
											item={lineItem}
											handleUpdateItem={handleUpdateItem}
											expandedItems={expandedItems}
											lineItems={lineItems}
											newLineItems={newLineItems}
											onAddChildItem={handleAddChildItem}
											onAddNewLineItem={onAddNewLineItem}
											onRemoveNewLineItem={onRemoveNewLineItem}
											level={0}
											onToggleItemExpansion={onToggleItemExpansion}
											onDeleteItem={handleDeleteItem}
											onSaveNewLineItem={onSaveNewLineItem}
											handleLinkDamageItems={handleLinkDamageItems}
										/>
									))}

									{rootNewItems.map((lineItem) => (
										<NewLineItemRow
											key={lineItem.id}
											onSave={onSaveNewLineItem}
											lineItem={lineItem}
											onRemove={onRemoveNewLineItem}
										/>
									))}
									<Restricted to={ROLE.WORK_PACKAGES_UPDATE}>
										<TableRow>
											<TableCell className="text-center py-2" colSpan={7}>
												<Button onClick={handleAddRow}>
													<PlusIcon />
													{t('common.actions.add')}
												</Button>
											</TableCell>
										</TableRow>
									</Restricted>
								</TableBody>
							</Table>
						</div>
					</div>
				</div>
			</div>
			<AddLineItemDialog
				onAddNewLineItem={onAddNewLineItem}
				open={isAddChoiceDialogOpen}
				setOpen={setIsAddChoiceDialogOpen}
				alreadyAddedDamagesIds={alreadyAddedDamagesIds}
			/>
			<DamageListDialog
				open={damageListOpen}
				setOpen={setDamageListOpen}
				onDamageSelect={onDamageSelect}
				alreadyAddedDamagesIds={alreadyAddedDamagesIds}
			/>
		</>
	);
}
